import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

// -- Simple Edit Modals (self-view) ----------------------------------------
// Covers the common pattern: one textarea or select + Save button that
// PATCHes the sitter and updates parent state.

export const TextAreaEditModal = ({
  isOpen,
  onClose,
  title,
  label,
  helperText,
  initialValue,
  maxLength,
  onSave,
}: ModalBase & {
  title: string;
  label: string;
  helperText?: string;
  initialValue: string;
  maxLength?: number;
  onSave: (value: string) => Promise<void>;
}): JSX.Element => {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setError('');
    }
  }, [isOpen, initialValue]);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave(value);
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <label className='block mb-2 font-semibold text-base'>{label}</label>
      <textarea
        className='w-full border border-neutral-300 rounded p-2'
        rows={6}
        maxLength={maxLength}
        value={value}
        onChange={(e): void => setValue(e.target.value)}
      />
      {helperText && <p className='text-sm text-neutral-600 mt-1'>{helperText}</p>}
      {maxLength && (
        <div className='text-sm text-neutral-600 mt-1'>
          {maxLength - value.length} characters remaining
        </div>
      )}
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};
